import { cloneElement, isValidElement, useRef, useState, type ReactElement } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar, type CalendarProps } from "@/components/ui/calendar";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

function toDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function fromDateInputValue(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

type DateFieldPopoverProps = {
  /** The trigger element (button/input) that opens the picker. Must accept an onClick prop. */
  trigger: ReactElement<{ onClick?: React.MouseEventHandler }>;
  selected: Date | undefined;
  onSelect: (date: Date | undefined) => void;
  disabled?: CalendarProps["disabled"];
  fromDate?: Date;
  toDate?: Date;
  align?: "start" | "center" | "end";
  contentClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * Drop-in replacement for a Popover+Calendar single-date picker.
 * On mobile it opens the device's native date picker instead of the in-app calendar grid.
 */
export function DateFieldPopover({
  trigger,
  selected,
  onSelect,
  disabled,
  fromDate,
  toDate,
  align = "start",
  contentClassName,
  open,
  onOpenChange,
}: DateFieldPopoverProps) {
  const isMobile = useIsMobile();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const inputRef = useRef<HTMLInputElement>(null);

  if (isMobile) {
    const openNativePicker: React.MouseEventHandler = (event) => {
      if (isValidElement(trigger)) {
        trigger.props.onClick?.(event);
      }
      const input = inputRef.current;
      if (!input) return;
      if (typeof input.showPicker === "function") {
        input.showPicker();
      } else {
        input.click();
      }
    };

    return (
      <span className="relative inline-flex">
        {cloneElement(trigger, { onClick: openNativePicker })}
        <input
          ref={inputRef}
          type="date"
          aria-hidden="true"
          tabIndex={-1}
          className="absolute inset-0 h-0 w-0 opacity-0"
          value={selected ? toDateInputValue(selected) : ""}
          min={fromDate ? toDateInputValue(fromDate) : undefined}
          max={toDate ? toDateInputValue(toDate) : undefined}
          onChange={(event) => {
            onSelect(event.target.value ? fromDateInputValue(event.target.value) : undefined);
          }}
        />
      </span>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      {/* Sempre portalado: renderizar em fluxo faz o calendário ser recortado
          pelo `overflow` do diálogo/scroll container que o contém. */}
      <PopoverContent className={cn("w-auto p-0", contentClassName)} align={align}>
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(date) => {
            onSelect(date);
            setOpen(false);
          }}
          disabled={disabled}
          fromDate={fromDate}
          toDate={toDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
